import React from 'react'
import { BrowserRouter, Routes, Route, Outlet, useLocation, useNavigate, Navigate, useParams } from 'react-router-dom'
import Navbar from './components/Navbar'
import Footer from './components/Footer'
import Home from './pages/Home'
import Catalog from './pages/Catalog'
import Product from './pages/Product'
import Cart from './pages/Cart'
import Checkout from './pages/Checkout'
import CheckoutSuccess from './pages/CheckoutSuccess'
import CheckoutCancel from './pages/CheckoutCancel'
import Taste from './pages/Taste'
import WhyCaladinhos from './pages/WhyCaladinhos'
import WhoWeAre from './pages/WhoWeAre'
import { CartProvider } from './state/CartContext'
import { I18nProvider, useI18n } from './state/I18nContext'
import Story from './pages/Story'
import News from './pages/News'
import Shop from './pages/Shop'
import Contact from './pages/Contact'
import { ENABLE_FUTURE_PAGES } from './config'
import WhatsAppFloat from './components/WhatsAppFloat'

// Layout wrapper that renders the chrome and nested routes
const Layout: React.FC = () => (
  <div className="min-h-screen flex flex-col" style={{ background: '#faf6ef', color: '#2a1f0e' }}>
    <Navbar />
    <main className="flex-1">
      <Outlet />
    </main>
    <Footer />
    <WhatsAppFloat />
  </div>
)

// Utility to compute preferred locale based on LS/URL/navigator
const PreferredRedirect: React.FC = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const { locale: ctxLocale, setLocale, detectPreferred } = useI18n()

  React.useEffect(() => {
    const preferred = detectPreferred()
    // If current URL already includes a locale and matches preferred, just ensure context is aligned
    const match = location.pathname.match(/^\/(pt|en|fr|es)(\/.*)?$/)
    const currentUrlLocale = match ? (match[1] as 'pt' | 'en' | 'fr' | 'es') : null
    const rest = match ? match[2] || '' : location.pathname
    if (!currentUrlLocale || currentUrlLocale !== preferred) {
      if (ctxLocale !== preferred) setLocale(preferred, { redirect: false })
      navigate(`/${preferred}${rest}${location.search}${location.hash}`, { replace: true })
    } else if (ctxLocale !== currentUrlLocale) {
      setLocale(currentUrlLocale as any, { redirect: false })
    }
  }, [])

  return null
}

const LocaleHomeRedirect: React.FC = () => {
  const { locale } = useParams()
  const safeLocale = locale === 'pt' || locale === 'en' || locale === 'fr' || locale === 'es' ? locale : 'pt'
  return <Navigate to={`/${safeLocale}/`} replace />
}

const App: React.FC = () => {
  return (
    <BrowserRouter>
      <I18nProvider>
        <CartProvider>
          <Routes>
            {/* Root and fallback redirect to preferred locale/prefixed path */}
            <Route path="/" element={<PreferredRedirect />} />
            <Route path="*" element={<PreferredRedirect />} />

            {/* Locale-prefixed app */}
            <Route path=":locale" element={<Layout />}>
              <Route index element={<Home />} />
              <Route path="catalog" element={<Catalog />} />
              <Route path="taste" element={<Taste />} />
              <Route path="why-caladinhos" element={<WhyCaladinhos />} />
              <Route path="who-we-are" element={<WhoWeAre />} />

              {ENABLE_FUTURE_PAGES ? (
                <>
                  <Route path="story" element={<Story />} />
                  <Route path="news" element={<News />} />
                  <Route path="shop" element={<Shop />} />
                  <Route path="contact" element={<Contact />} />
                </>
              ) : (
                <>
                  <Route path="story" element={<LocaleHomeRedirect />} />
                  <Route path="news" element={<LocaleHomeRedirect />} />
                  <Route path="shop" element={<LocaleHomeRedirect />} />
                  <Route path="contact" element={<LocaleHomeRedirect />} />
                </>
              )}
              <Route path="product/:id" element={<Product />} />
              <Route path="cart" element={<Cart />} />
              <Route path="checkout" element={<Checkout />} />
              <Route path="checkout/success" element={<CheckoutSuccess />} />
              <Route path="checkout/cancel" element={<CheckoutCancel />} />
              <Route path="*" element={<LocaleHomeRedirect />} />
            </Route>
          </Routes>
        </CartProvider>
      </I18nProvider>
    </BrowserRouter>
  )
}

export default App
